<template>
  <b-modal
    ref="modal"
    id="edit-client-modal"
    title="Modal Variants"
    hide-header
    hide-footer
    centered
    @hide="onHide"
    @show="onShow"
    static
  >
    <div class="right" v-if="isMobile">
      <div class="icon">
        <center>
          <font-awesome-icon @click="() => off()" :icon="faTimes" />
        </center>
      </div>
    </div>
    <div class="header">
      <span>{{ $t('UPDATE_CLIENT_INFO') }}</span>
    </div>
    <b-container class="body">
      <b-row>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="firstName"
            ref="firstNameInput"
            :label="$t('FIRST_NAME')"
            dynamic-label
            required
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="lastName"
            ref="lastNameInput"
            :label="$t('LAST_NAME')"
            dynamic-label
            required
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="primaryPhoneNumber"
            type="phoneNumber"
            ref="primaryPhoneNumberInput"
            :label="$t('PRIMARY_PHONE_NUMBER')"
            dynamic-label
            required
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="secondaryPhoneNumber"
            ref="secondaryPhoneNumberInput"
            :label="$t('SECONDARY_PHONE_NUMBER')"
            dynamic-label
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <select-input
            v-model="gender"
            :items="genders"
            ref="genderInput"
            :label="$t('GENDER')"
            dynamic-label
            class="form-select-input"
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <toggle-box ref="dateOfBirthBox">
            <template v-slot:control>
              <text-input
                v-model="dateOfBirth"
                @focus="() => showCalendarDateOfBirth(true)"
                ref="dateOfBirthInput"
                :label="$t('DATE_OF_BIRTH')"
                dynamic-label
                required
                class="date-of-birth"
              >
                <template v-slot:icon>
                  <font-awesome-icon class="icon" :icon="faCalendarAlt" />
                </template>
              </text-input>
            </template>
            <template v-slot:content>
              <calendar
                :date="dateOfBirth || undefined"
                @dateChange="setDateOfBirth"
                :yearRange="[moment().subtract(100, 'year').year(), moment().subtract(15, 'year').year()]"
              />
            </template>
          </toggle-box>
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="citizenId"
            ref="citizenIdInput"
            :label="$t('CITIZEN_ID')"
            dynamic-label
            required
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <select-input
            :items="cities"
            v-model="city"
            ref="cityInput"
            :label="$t('PROVINCE_MUNICIPALITY')"
            dynamic-label
            required
            class="form-select-input"
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <select-input
            :items="districts"
            v-model="district"
            ref="districtInput"
            :label="$t('DISTRICT_PROVINCIAL_CITY')"
            dynamic-label
            class="form-select-input"
          />
        </b-col>
        <b-col cols="12" class="input-wrapper">
          <text-input
            v-model="address"
            ref="addressInput"
            :label="$t('ADDRESS')"
            dynamic-label
          />
        </b-col>

        <b-col lg="4" md="4" sm="12" class="input-wrapper">
          <text-input
            v-model="zaloId"
            ref="zaloIdInput"
            :label="$t('ZALO_ID')"
            dynamic-label
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <div class="btns">
            <primary-button @click="off()" blue :text="$t('CANCEL')" />
            <primary-button @click="save()" :text="$t('SAVE')" />
          </div>
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>
<script>
import { watch, ref, computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendarAlt, faTimes } from '@fortawesome/free-solid-svg-icons';

import { DATE_FORMAT, GENDERS } from '@/consts';

import PrimaryButton from '@/components/PrimaryButton';
import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';
import ToggleBox from '@/components/ToggleBox';
import Calendar from '@/components/Calendar';

const { useGetters: useModalGetters, useActions: useModalActions } = createNamespacedHelpers('modal');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useState, useActions } = createNamespacedHelpers('clientDetail');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'EditClientInfo',
  components: {
    TextInput,
    SelectInput,
    ToggleBox,
    Calendar,
    FontAwesomeIcon,
    PrimaryButton
  },

  setup(props, { root }) {
    const modal = ref(null);

    const { isEditClientModalActive: isOpen } = useModalGetters(['isEditClientModalActive']);
    const { toggleEditClientModal: toggleEditClient } = useModalActions(['toggleEditClientModal', 'toggleConfirmModal']);
    const { info, error } = useFlashActions(['info', 'error']);
    const { updateClient } = useActions(['updateClient']);
    const { targetingClient: client } = useState(['targetingClient']);
    const { isMobile, locations } = useAppState(['isMobile', 'locations']);

    const primaryPhoneNumber = ref('');
    const secondaryPhoneNumber = ref('');

    const firstName = ref('');
    const lastName = ref('');

    const dateOfBirth = ref(null);
    const dateOfBirthBox = ref(null);
    const dateOfBirthInput = ref(null);

    const citizenId = ref('');
    const zaloId = ref('');

    const gender = ref('');
    const address = ref('');
    const district = ref(null);
    const city = ref(1);

    const cities = computed(() => locations.value.filter((item) => (
      item.type.level === 1
    )).map((item) => ({
      text: item.name,
      value: item.id
    })));

    const districts = computed(() => locations.value.filter((item) => (
      (item.type.level === 2)
      && (item.parent === city.value)
    )).map((item) => ({
      text: item.name,
      value: item.id
    })));

    const genders = Object.keys(GENDERS).map((item) => ({
      text: root.$t(GENDERS[item]),
      value: GENDERS[item]
    }));

    const setDateOfBirth = (val) => {
      dateOfBirth.value = val;
      dateOfBirthBox.value.toggle(false);
    };

    const showCalendarDateOfBirth = () => {
      dateOfBirthBox.value.toggle(true);
      dateOfBirthInput.value.blur();
    };

    const onShow = () => {
      primaryPhoneNumber.value = client.value.primaryPhoneNumber;
      secondaryPhoneNumber.value = client.value.secondaryPhoneNumber;
      firstName.value = client.value.firstName;
      lastName.value = client.value.lastName;
      citizenId.value = client.value.citizenId;
      zaloId.value = client.value.zaloId;
      gender.value = client.value.gender;
      address.value = client.value.address;
      dateOfBirth.value = moment(client.value.dateOfBirth || undefined).format(DATE_FORMAT);

      const loc = locations.value.find((l) => (l.id === client.value.locationId));
      if (loc) {
        if (loc.type.level === 1) {
          city.value = loc.id;
          district.value = null;
        } else if (loc.type.level === 2) {
          district.value = loc.id;
          city.value = loc.parent;
        }
      }
    };

    const off = () => {
      modal.value.hide();
    };

    const save = async () => {
      try {
        const data = {
          firstName: firstName.value,
          lastName: lastName.value,
          primaryPhoneNumber: primaryPhoneNumber.value,
          secondaryPhoneNumber: secondaryPhoneNumber.value,
          citizenId: citizenId.value,
          zaloId: zaloId.value || null,
          address: address.value,
          city: city.value,
          district: district.value,
          gender: gender.value,
          dateOfBirth: moment(dateOfBirth.value).format(DATE_FORMAT),
          locationId: district.value || city.value
        };

        await updateClient({ id: client.value.id, data });
        info('SUCCESSFULLY_UPDATED_CLIENT');

        toggleEditClient(false);
      } catch(err) {
        error(err);
      }
    };

    const onHide = () => {
      toggleEditClient(false);
    };

    watch(isOpen, (newVal) => {
      if (newVal) {
        modal.value.show();
      } else modal.value.hide();
    });

    return {
      modal,
      onShow,
      onHide,
      off,
      faCalendarAlt,
      gender,
      primaryPhoneNumber,
      secondaryPhoneNumber,
      firstName,
      lastName,
      citizenId,
      zaloId,
      dateOfBirthBox,
      dateOfBirthInput,
      dateOfBirth,
      showCalendarDateOfBirth,
      setDateOfBirth,
      address,
      district,
      city,
      cities,
      districts,
      genders,
      save,
      isMobile,
      faTimes,
      moment,
    };
  },

};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#edit-client-modal {
  .modal-dialog {
    max-width: 790px;
  }

  .header {
    text-align: center;
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 25px;
    font-weight: bold;
  }

  .body {
    margin-top: 20px;
    margin-bottom: 20px;

    .input-wrapper {
      padding-left: 7.5px;
      padding-right: 7.5px;
    }

    .input {
      margin-bottom: 15px;
    }

    .btns {
      margin-top: 15px;
      text-align: right;

      .primary-button {
        border-radius: 4px;

        &:first-child {
          margin-right: 15px;
        }
      }
    }

    .display,
    .text-input > input {
      border-radius: 4px;
    }

    .form-select-input {
      width: 100%;
    }
  }
};
</style>
